import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { 
  MoreHorizontal, 
  Calendar, 
  MessageSquare, 
  CheckSquare,
  AlertCircle,
  Edit,
  Copy,
  Trash2,
  ArrowRight
} from "lucide-react";
import { cn } from "@/lib/utils";

const STATUS_CONFIG = {
  "To Do": { bg: "bg-gray-100", text: "text-gray-700", border: "border-l-gray-400" },
  "In Progress": { bg: "bg-blue-100", text: "text-blue-700", border: "border-l-blue-500" },
  "In Review": { bg: "bg-purple-100", text: "text-purple-700", border: "border-l-purple-500" },
  "Blocked": { bg: "bg-red-100", text: "text-red-700", border: "border-l-red-500" },
  "Completed": { bg: "bg-green-100", text: "text-green-700", border: "border-l-green-500" },
  "Pending Approval": { bg: "bg-amber-100", text: "text-amber-700", border: "border-l-amber-500" }
};

const PRIORITY_CONFIG = {
  "Urgent": { bg: "bg-red-100", text: "text-red-700", icon: "🔴" },
  "High": { bg: "bg-orange-100", text: "text-orange-700", icon: "🟠" },
  "Medium": { bg: "bg-amber-100", text: "text-amber-700", icon: "🟡" },
  "Low": { bg: "bg-green-100", text: "text-green-700", icon: "🟢" }
};

const TaskCard = ({ 
  task, 
  projects, 
  teamMembers, 
  onStatusChange, 
  onPriorityChange,
  onDelete,
  onDuplicate,
  onSelect,
  isSelected,
  onComplete,
  onClick
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG["To Do"];
  const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG["Medium"];
  const project = projects?.find(p => p.id === task.projectId);
  const assignee = teamMembers?.find(m => m.id === task.assignedTo);
  
  const subtasks = task.subtasks || [];
  const completedSubtasks = subtasks.filter(st => st.completed).length;
  
  const isOverdue = (() => {
    if (!task.dueDate || task.status === "Completed") return false;
    let dueDate;
    try {
      if (typeof task.dueDate === 'string') {
        dueDate = new Date(task.dueDate);
      } else if (task.dueDate && typeof task.dueDate.toDate === 'function') {
        dueDate = task.dueDate.toDate();
      } else if (task.dueDate && typeof task.dueDate === 'object' && task.dueDate.seconds) {
        dueDate = new Date(task.dueDate.seconds * 1000);
      } else {
        dueDate = new Date(task.dueDate);
      }
      return dueDate < new Date();
    } catch {
      return false;
    }
  })();
  const isCompleted = task.status === "Completed";
  
  const formatDueDate = (date) => {
    if (!date) return null;
    let dueDate;
    try {
      if (typeof date === 'string') {
        dueDate = new Date(date);
      } else if (date && typeof date.toDate === 'function') {
        dueDate = date.toDate();
      } else if (date && typeof date === 'object' && date.seconds) {
        dueDate = new Date(date.seconds * 1000);
      } else {
        dueDate = new Date(date);
      }
      if (isNaN(dueDate.getTime())) return null;
    } catch {
      return null;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const checkDate = new Date(dueDate);
    checkDate.setHours(0, 0, 0, 0);
    
    if (checkDate.toDateString() === today.toDateString()) return "Today";
    if (checkDate.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    return dueDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = [
      "from-blue-500 to-blue-600",
      "from-purple-500 to-purple-600",
      "from-pink-500 to-pink-600",
      "from-green-500 to-green-600",
      "from-amber-500 to-amber-600"
    ];
    const index = name?.charCodeAt(0) || 0;
    return colors[index % colors.length];
  };

  const menuItems = [
    { icon: Edit, label: "Edit Task", action: onClick },
    { icon: Copy, label: "Duplicate Task", action: () => onDuplicate && onDuplicate(task) },
    { label: "Change Status", action: () => {} },
    { label: "Change Priority", action: () => {} },
    { label: "Move to Project", action: () => {} },
    { icon: Trash2, label: "Delete Task", action: () => onDelete && onDelete(task), danger: true }
  ];

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    const newStatus = isCompleted ? "To Do" : "Completed";
    onComplete && onComplete(task.id, newStatus);
  };

  return (
    <Card 
      className={cn(
        "mb-2 transition-all cursor-pointer border-l-4 hover:bg-slate-50",
        statusConfig.border,
        isHovered && "shadow-md bg-slate-50",
        isSelected && "ring-2 ring-navy"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick && onClick(task)}
    >
      <div className="py-2 px-2 sm:py-3 sm:px-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <div className="flex items-center gap-2">
          <Checkbox 
            checked={isCompleted}
            onChange={handleCheckboxChange}
            disabled={task.status === "Pending Approval"}
            className={cn(
              "w-4 h-4 sm:w-5 sm:h-5 rounded-full",
              isCompleted && "bg-green-500 border-green-500"
            )}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <span className={cn(
            "font-semibold text-navy text-sm sm:text-base break-words",
            isCompleted && "line-through text-gray-400"
          )}>
            {task.title}
          </span>
        </div>
        
        {project && (
          <div 
            className="hidden sm:block min-w-[100px] px-2 py-1 rounded-full text-xs font-medium"
            style={{ backgroundColor: `${project.color}20`, color: project.color }}
          >
            {project.name}
          </div>
        )}
        
        <div className={cn("hidden sm:block min-w-[80px] text-sm", priorityConfig.text)}>
          {priorityConfig.icon} {task.priority || "Medium"}
        </div>
        
        <div className="hidden sm:block min-w-[90px]">
          <Badge className={cn(statusConfig.bg, statusConfig.text)}>
            {task.status}
          </Badge>
        </div>
        
        <div className="hidden sm:block min-w-[60px]">
          {assignee ? (
            <div 
              className={cn(
                "w-8 h-8 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-xs font-medium",
                getAvatarColor(assignee.name)
              )}
              title={assignee.name}
            >
              {getInitials(assignee.name)}
            </div>
          ) : (
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
              <span className="text-slate-400 text-xs">?</span>
            </div>
          )}
        </div>
        
        <div className={cn(
          "hidden sm:block min-w-[80px] text-sm flex items-center gap-1",
          isOverdue ? "text-red-600 font-medium" : "text-gray-500"
        )}>
          {isOverdue && <AlertCircle className="w-3 h-3" />}
          {formatDueDate(task.dueDate) || "-"}
        </div>
        
        <div className="flex sm:hidden items-center gap-1.5 text-[10px]">
          <span className={cn("font-medium", priorityConfig.text)}>
            {task.priority === "Low" ? "🟢" : task.priority === "Medium" ? "🟡" : task.priority === "High" ? "🟠" : task.priority === "Urgent" ? "🔴" : "🟡"}
          </span>
          <span className={cn("text-[9px] whitespace-nowrap", statusConfig.text)}>
            {task.status === "In Progress" ? "In Progress" : task.status === "To Do" ? "To Do" : task.status === "Completed" ? "Done" : task.status === "Pending Approval" ? "Pending App" : task.status === "In Review" ? "In Review" : task.status === "Blocked" ? "Blocked" : task.status}
          </span>
          {assignee ? (
            <span 
              className={cn(
                "text-[9px] font-medium text-navy truncate max-w-[60px]",
                getAvatarColor(assignee.name)
              )}
              title={assignee.name}
            >
              {assignee.firstName || assignee.name?.split(" ")[0] || "Unassigned"}
            </span>
          ) : (
            <span className="text-[9px] text-gray-400">?</span>
          )}
          {task.dueDate && (
            <span className={cn(
              "shrink-0",
              isOverdue ? "text-red-600 font-medium" : "text-gray-500"
            )}>
              {formatDueDate(task.dueDate)}
            </span>
          )}
        </div>
        
        {subtasks.length > 0 && (
          <div className="hidden sm:flex min-w-[60px] text-sm text-gray-500 items-center gap-1">
            <CheckSquare className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="text-xs">{completedSubtasks}/{subtasks.length}</span>
          </div>
        )}
        
        <div className="hidden sm:flex min-w-[40px] text-sm text-gray-500 items-center gap-1">
          <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="text-xs">{task.comments?.length || 0}</span>
        </div>
        
        <div className="min-w-[30px] sm:min-w-[40px] flex justify-end relative" data-menu>
          <Button 
            variant="ghost" 
            size="icon" 
            className={cn(
              "h-8 w-8 transition-opacity",
              isHovered ? "opacity-100" : "opacity-0"
            )}
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
          >
            <MoreHorizontal className="w-4 h-4" />
          </Button>
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20">
              {menuItems.map((item, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    item.action && item.action();
                    setShowMenu(false);
                  }}
                  className={cn(
                    "w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-slate-50",
                    item.danger ? "text-red-600" : "text-navy"
                  )}
                >
                  {item.icon && <item.icon className="w-4 h-4" />}
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default TaskCard;